import { useEffect, useMemo, useState } from "react";
import { MapContainer, TileLayer, CircleMarker, Popup, GeoJSON, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import * as topojson from "topojson-client";
import worldTopo from "world-atlas/countries-110m.json";
import { inrShort } from "@/pages/Dashboard";

const COUNTRY_ALIAS = { "United Arab Emirates": "United Arab Emirates", "Tanzania": "Tanzania", "United States": "United States of America", "United Kingdom": "United Kingdom" };

function FitBounds({ points }) {
  const map = useMap();
  useEffect(() => {
    const pts = points.filter((p) => p.lat != null);
    if (pts.length) map.fitBounds(pts.map((p) => [p.lat, p.lng]), { padding: [40, 40], maxZoom: 6 });
  }, [points, map]);
  return null;
}

function FitGeo({ data, filter, filterKey = "" }) {
  const map = useMap();
  useEffect(() => {
    if (!data) return;
    const feats = filter ? data.features.filter(filter) : data.features;
    if (!feats.length) return;
    map.fitBounds(L.geoJSON({ type: "FeatureCollection", features: feats }).getBounds(), { padding: [10, 10] });
  }, [data, filterKey, map]); // eslint-disable-line react-hooks/exhaustive-deps
  return null;
}

const radius = (v, max) => (max ? 8 + Math.sqrt(v / max) * 34 : 8);

export const SalesBubbleMap = ({ locations, onSelect }) => {
  const max = Math.max(0, ...locations.map((l) => l.sales_value));
  return (
    <MapContainer center={[20, 60]} zoom={3} scrollWheelZoom className="h-[520px] w-full rounded-lg z-0" data-testid="sales-bubble-map">
      <TileLayer attribution="Tiles &copy; Esri — Esri, HERE, Garmin, OpenStreetMap contributors" url="https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Light_Gray_Base/MapServer/tile/{z}/{y}/{x}" maxZoom={16} />
      <TileLayer url="https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Light_Gray_Reference/MapServer/tile/{z}/{y}/{x}" maxZoom={16} />
      <FitBounds points={locations} />
      {locations.filter((l) => l.lat != null).map((l) => (
        <CircleMarker key={l.label} center={[l.lat, l.lng]} radius={radius(l.sales_value, max)}
          pathOptions={{ color: l.country === "India" ? "#075985" : "#b45309", fillColor: l.country === "India" ? "#0ea5e9" : "#f59e0b", fillOpacity: 0.6, weight: 1.5 }}
          eventHandlers={{ click: () => onSelect?.(l) }}>
          <Popup>
            <div className="text-xs min-w-[200px]">
              <p className="font-bold text-slate-900">{l.label}</p>
              <p className="text-slate-600">{inrShort(l.sales_value)} · {l.share_pct}% · {l.invoices} invoices{l.precision !== "city" ? ` · ${l.precision}-level` : ""}</p>
              <ul className="mt-1 space-y-0.5">{l.customers.slice(0, 6).map((c) => <li key={c.name} className="flex justify-between gap-2"><span className="truncate">{c.name}</span><span className="font-mono">{inrShort(c.sales_value)}</span></li>)}</ul>
            </div>
          </Popup>
        </CircleMarker>
      ))}
    </MapContainer>
  );
};

const shade = (v, max) => {
  if (!v) return "#f1f5f9";
  const t = Math.min(1, Math.sqrt(v / max));
  const c = [224 - 190 * t, 242 - 160 * t, 254 - 110 * t];
  return `rgb(${c.map(Math.round).join(",")})`;
};

export const SalesChoropleth = ({ byState, byCountry, onSelect }) => {
  const [india, setIndia] = useState(null);
  useEffect(() => { fetch("/geo/india_states.json").then((r) => r.json()).then(setIndia).catch(() => setIndia({ type: "FeatureCollection", features: [] })); }, []);
  const world = useMemo(() => topojson.feature(worldTopo, worldTopo.objects.countries), []);
  const stateMap = Object.fromEntries(byState.map((s) => [s.name, s]));
  const countryMap = Object.fromEntries(byCountry.map((c) => [COUNTRY_ALIAS[c.name] || c.name, c]));
  const maxS = Math.max(0, ...byState.map((s) => s.sales_value));
  const maxC = Math.max(0, ...byCountry.filter((c) => c.name !== "India").map((c) => c.sales_value));
  const styleFor = (lookup, max) => (f) => {
    const d = lookup[f.properties.name];
    return { fillColor: shade(d?.sales_value, max), fillOpacity: 0.9, color: "#94a3b8", weight: 0.6 };
  };
  const bind = (lookup, kind) => (f, layer) => {
    const d = lookup[f.properties.name];
    layer.bindTooltip(`<b>${f.properties.name}</b><br/>${d ? `${inrShort(d.sales_value)} · ${d.share_pct}% · ${d.customer_count} customers` : "No sales"}`, { sticky: true });
    if (d) layer.on("click", () => onSelect?.({ kind, name: f.properties.name, ...d }));
  };
  return (
    <div className="grid grid-cols-1 xl:grid-cols-5 gap-4" data-testid="sales-choropleth">
      <div className="xl:col-span-3 rounded-lg border border-slate-200 overflow-hidden">
        <p className="px-3 py-2 text-xs font-semibold uppercase tracking-wider text-slate-500 bg-slate-50 border-b">India — sales by state</p>
        <MapContainer center={[22.5, 80]} zoom={4} scrollWheelZoom={false} zoomControl={false} dragging={false} doubleClickZoom={false} attributionControl={false} className="h-[480px] w-full bg-white" style={{ background: "#fff" }} data-testid="sales-india-map">
          {india && <GeoJSON key={byState.length} data={india} style={styleFor(stateMap, maxS)} onEachFeature={bind(stateMap, "state")} />}
          <FitGeo data={india} />
        </MapContainer>
      </div>
      <div className="xl:col-span-2 rounded-lg border border-slate-200 overflow-hidden">
        <p className="px-3 py-2 text-xs font-semibold uppercase tracking-wider text-slate-500 bg-slate-50 border-b">World — export sales by country</p>
        <MapContainer center={[15, 50]} zoom={2} scrollWheelZoom={false} zoomControl={false} dragging={false} doubleClickZoom={false} attributionControl={false} className="h-[480px] w-full" style={{ background: "#fff" }} data-testid="sales-world-map">
          <GeoJSON key={byCountry.length} data={world} style={(f) => f.properties.name === "India" ? { fillColor: "#0369a1", fillOpacity: 0.35, color: "#94a3b8", weight: 0.5 } : styleFor(countryMap, maxC)(f)} onEachFeature={bind(countryMap, "country")} />
          <FitGeo data={world} filterKey={Object.keys(countryMap).join("|")} filter={(f) => f.properties.name in countryMap || f.properties.name === "India"} />
        </MapContainer>
      </div>
    </div>
  );
};
